// Copyright (c) Docugami, Inc. All rights reserved.

import styled from "styled-components"
import { DgInputBox } from 'components/common/DgCommonStyledComponents';
import { emptyGray, greyText, homeGray } from 'components/shell/presentation/AppStyles';
import { dgFontFamily } from 'components/common/Fonts';
import { useSelector } from "react-redux";
import { AppState } from "store";
import { getClusterById } from 'store/state_helpers';
import { RenamedWorkPathSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { reportErrorEncountered } from "store/authentication/actions";
import { ITooltipHostStyles, TooltipHost, TooltipOverflowMode } from "@fluentui/react/lib/Tooltip";
import { useKeyPressHandler } from "hooks/KeyPressHandler";
import { useMemo, useState } from "react";
import { WorkPath } from "networking/WorkPathApi";
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
    IContextualMenuProps,
    DefaultButton,
    IContextualMenuItem,
    DirectionalHint,
    Stack,
    Link
} from "@fluentui/react";
import {
    useEditReportCommand,
    useConnectorInfoCommand,
    useStorageProviderExportCommand,
    useExcelDownloadCommand,
    useAbstractDownloadCommand,
    IWorkPathCommandItem,
    useDgmlDownloadCommand,
    useAbstractTemplateDownloadCommand,
    useAbstractTemplateUploadCommand,
    useAbstractTemplateDefaultDownloadCommand,
    useExcelPreviewDownloadCommand,
    useDownloadFlaggedDocumentsCommand,
    usePreviewAbstractDownloadCommand,
    useDownloadReportDetailsDebugCommand,
    useDownloadDocumentCommand,
    useRemoveDocumentCommand,
    usePreviewSingleDocumentExcelDownloadCommand,
    useSingleDocumentExcelDownloadCommand,
    useWpDocsetModelStatsCommand
} from "hooks/WorkPathCommands";
import {
    EditReportModal,
    ConnectorInfoModal,
    WorkPathStorageProviderExportModal,
    AbstractTemplateUploadModal,
    DocsetModelStatsModal
} from "./WorkPathCommandComponents";
import { IconRenderer } from "./IconRenderer";
import { useWaitCursor } from "hooks/WaitCursor";
import { isSingleDocumentReportView } from "components/reports/ReportsHelpers";
import { RestrictedScreenView } from "components/common/RestrictedScreenView";
import { RepublishButton } from "./RepublishButton";
import { selectReportDocumentId, selectReportViewMode } from "store/reports/selectors";
import { useNavigationService } from "hooks/navigationService";
import { useRenameWorkPathMutation } from "store/work_path/workPathSlice";

const Label = styled.span`
    font-family: ${dgFontFamily};
    text-transform: uppercase;
    color: ${greyText};
    margin-right: 10px;
    display: flex;
    align-items: center;
    white-space: pre;
`;

const InfoTextContainer = styled.div`
    flex-basis: 10%;
    display: flex;
    align-items: center;
    height: 100%;
`;

export interface HeaderInfoSectionProps {
    label: string;
    children?: string | JSX.Element;
}

export const HeaderInfoSection = (props: HeaderInfoSectionProps): JSX.Element => {
    return (
        <InfoTextContainer>
            <Label>{props.label}:</Label>
            {props.children}
        </InfoTextContainer>
    )
}

const overflowTooltipStyles: ITooltipHostStyles = {
    root: {
        maxWidth: "20ch",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        alignContent: "center"
    }
}

const DocSetNameLink = styled(Link)`
    color: black;
`;

export const WorkPathHeaderDocSetName = (props: { clusterId: string }) => {
    const clusterState = useSelector((state: AppState) => getClusterById(state, props.clusterId));
    const { getDeepLinkToDocSetPage } = useNavigationService();

    return (
        <HeaderInfoSection label="doc set">
            <TooltipHost overflowMode={TooltipOverflowMode.Self} content={clusterState?.clusterInfo?.label} styles={overflowTooltipStyles}>
                <DocSetNameLink href={getDeepLinkToDocSetPage(props.clusterId)}>{clusterState?.clusterInfo?.label}</DocSetNameLink>
            </TooltipHost>
        </HeaderInfoSection>
    )
}

export const WorkPathHeaderEditableName = (props: { workPath: WorkPath }) => {
    return (
        <HeaderInfoSection label={'name'}>
            <EditableWorkPathName workPath={props.workPath} />
        </HeaderInfoSection>
    )
}

const StyledDgInputBox = styled(DgInputBox)`
    margin: 0;
    padding: 0;
    font-size: 20px;
    font-weight: 300;
`;

interface EditableWorkPathNameProps {
    workPath: WorkPath;
}

const EditableWorkPathName = (props: EditableWorkPathNameProps): JSX.Element => {
    const [editedName, setEditedName] = useState<string>();

    const workPathName = useSelector((state: AppState) => state.workPaths.currentWorkPath?.name ?? '');
    const [trigger] = useRenameWorkPathMutation();

    const dispatch = useAppDispatch();

    async function confirmName() {
        const targetName = editedName?.trim();
        setEditedName(undefined);
        if (!!targetName) {
            const previousName = workPathName;
            try {
                Telemetry.trackEvent<RenamedWorkPathSchema>({
                    workPathId: props.workPath.id,
                    previousName: previousName,
                    name: targetName,
                    eventName: TelemetryEvent.RenamedWorkPath
                });

                trigger({
                    workPathId: props.workPath.id,
                    name: targetName
                });
            }
            catch (error) {
                dispatch(reportErrorEncountered(`Failed to rename WorkPath. ID: ${props.workPath.id}`, error as Error));
            }
        }
    }

    useKeyPressHandler(!!editedName ? 'Escape' : undefined, () => setEditedName(undefined));

    if (editedName !== undefined && !!workPathName) {
        return (
            <StyledDgInputBox
                onBlur={confirmName}
                value={editedName}
                onChange={({ target: { value } }) => setEditedName(value)}
                autoFocus={true}
                onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                        confirmName();
                    }
                }}
            />
        )
    }

    return (
        <TooltipHost overflowMode={TooltipOverflowMode.Self} content={workPathName} styles={overflowTooltipStyles}>
            <span onClick={() => setEditedName(workPathName)} style={{ cursor: 'pointer' }}>{editedName || workPathName}</span>
        </TooltipHost>
    )
}

export interface WorkPathCommandBarProps {
    className?: string;
}

export const WorkPathCommandBar = ({ className }: WorkPathCommandBarProps) => {
    const folderId = useAppSelector(selectReportDocumentId);
    const viewMode = useAppSelector(selectReportViewMode);

    const defaultAbstractTemplateDownloadCommand = useAbstractTemplateDefaultDownloadCommand();
    const abstractTemplateDownloadCommand = useAbstractTemplateDownloadCommand();
    const abstractTemplateUploadCommand = useAbstractTemplateUploadCommand();
    const templateCommands = [
        defaultAbstractTemplateDownloadCommand,
        abstractTemplateDownloadCommand,
        abstractTemplateUploadCommand,];

    const connectorInfoCommand = useConnectorInfoCommand();
    const storageProviderExportCommand = useStorageProviderExportCommand();
    const excelDownloadCommand = useExcelDownloadCommand();
    const diligenceDocsDownloadCommand = useDownloadFlaggedDocumentsCommand();
    const abstractDownloadCommand = useAbstractDownloadCommand();
    const singleDocumentExcelDownloadCommand = useSingleDocumentExcelDownloadCommand();
    const exportCommands = [
        connectorInfoCommand,
        storageProviderExportCommand,
        excelDownloadCommand,
        diligenceDocsDownloadCommand,
        abstractDownloadCommand,
        singleDocumentExcelDownloadCommand,]

    const editReport = useEditReportCommand();
    const dgmlDownloadCommand = useDgmlDownloadCommand(isSingleDocumentReportView(viewMode) ? folderId : '');
    const singleDocumentDownloadCommand = useDownloadDocumentCommand(isSingleDocumentReportView(viewMode) ? folderId : '');
    const { command: removeSingleDocumentCommand, overlay: removeSingleDocumentCommandOverlay } = useRemoveDocumentCommand(isSingleDocumentReportView(viewMode) ? folderId : '');
    const excelPreviewDownloadCommand = useExcelPreviewDownloadCommand();
    const previewAbstracDownloadCommand = usePreviewAbstractDownloadCommand();
    const previewSingleDocumentExcelDownloadCommand = usePreviewSingleDocumentExcelDownloadCommand()
    const debugDetailsCommand = useDownloadReportDetailsDebugCommand();
    const docsetModelStatsCommand = useWpDocsetModelStatsCommand();

    const sharedOverflowButtonProps = {
        iconName: "MoreVertical",
        minWidth: 25,
        background: "transparent !important",
        color: homeGray,
        hideCaret: true,
    };

    return (
        <div className={className}>
            <Stack horizontal tokens={{ childrenGap: 5 }}  >
                <RepublishButton />
                <RestrictedScreenView maxWidth={1000}>
                    <Stack horizontal tokens={{ childrenGap: 5 }}  >
                        <CommandsMenuButton
                            label={'Template'}
                            minWidth={100}
                            commands={templateCommands} />
                        <CommandsMenuButton
                            label={'Export'}
                            minWidth={85}
                            commands={exportCommands} />
                    </Stack>
                </RestrictedScreenView>
            </Stack>
            <RestrictedScreenView maxWidth={1000}>
                <CommandsMenuButton
                    {...sharedOverflowButtonProps}
                    minWidth={14}
                    commands={[
                        editReport,
                        docsetModelStatsCommand,
                        dgmlDownloadCommand,
                        singleDocumentDownloadCommand,
                        removeSingleDocumentCommand,
                        excelPreviewDownloadCommand,
                        previewAbstracDownloadCommand,
                        previewSingleDocumentExcelDownloadCommand,
                        debugDetailsCommand]} />
            </RestrictedScreenView>
            <RestrictedScreenView minWidth={1000}>
                <CommandsMenuButton
                    {...sharedOverflowButtonProps}
                    minWidth={14}
                    commands={[
                        editReport,
                        docsetModelStatsCommand,
                        ...templateCommands,
                        ...exportCommands,
                        dgmlDownloadCommand,
                        singleDocumentDownloadCommand,
                        removeSingleDocumentCommand,
                        excelPreviewDownloadCommand,
                        previewAbstracDownloadCommand,
                        previewSingleDocumentExcelDownloadCommand,
                        debugDetailsCommand]} />
            </RestrictedScreenView>
            <AbstractTemplateUploadModal command={abstractTemplateUploadCommand} />
            <DocsetModelStatsModal command={docsetModelStatsCommand} />
            <EditReportModal command={editReport} />
            <ConnectorInfoModal command={connectorInfoCommand} />
            <WorkPathStorageProviderExportModal command={storageProviderExportCommand} />
            {removeSingleDocumentCommandOverlay}
        </div>
    )
}

const IconContainer = styled.div`
    margin: 0 3px;
    width: 25px;
    height: 25px;
`;

interface CommandsMenuButtonProps {
    label?: string;
    iconName?: string;
    commands: IWorkPathCommandItem[];
    children?: JSX.Element | JSX.Element[];
    dataAutomationId?: string;
    color?: string;
    minWidth?: number;
    hideCaret?: boolean;
    background?: string;
}

const CommandsMenuButton = (props: CommandsMenuButtonProps): JSX.Element | null => {
    const commands = useMemo(() => props.commands.filter(x => !!x.isVisible), [props.commands]);
    const hasActiveCommand = useMemo(() => !!commands.find(c => !!c.isActive), [commands]);

    useWaitCursor(hasActiveCommand);

    function renderIcon({ iconDescriptor, disabled }: IContextualMenuItem) {
        if (!!iconDescriptor) {
            iconDescriptor.defaultColor = !!disabled ? `${iconDescriptor.defaultColor}66` : iconDescriptor.defaultColor;
        }
        return (
            <IconContainer>
                {!!iconDescriptor && <IconRenderer source={iconDescriptor} width={iconDescriptor.width * 20} height={iconDescriptor.height * 20} containerWidth={20} containerHeight={20} />}
            </IconContainer>
        )
    }

    const menuProps: IContextualMenuProps = {
        shouldFocusOnMount: true,
        items: commands.map((c, index) => ({
            key: c.key,
            text: c.name,
            disabled: c.disabled,
            onClick: c.onClick,
            'data-automation-id': c.dataAutomationId || undefined,
            iconProps: !!c.iconDescriptor ? {} : undefined,
            onRenderIcon: () => renderIcon(c),
            itemProps: {
                styles: {
                    linkContent: {
                        padding: '10px 0'
                    },
                    root: {
                        height: 'fit-content',
                        padding: '0 8px',
                        borderTop: index !== 0 ? `1px solid ${emptyGray}` : ''
                    }
                }
            }
        })),
        onMenuOpened() {
            commands.forEach(c => c.onOverflowOpened?.());
        },
        calloutProps: {
            styles: {
                calloutMain: {
                    borderRadius: '10px'
                },
                root: {
                    borderRadius: '10px'
                }
            }
        },
        directionalHint: DirectionalHint.bottomRightEdge,
        useTargetAsMinWidth: true,
    }

    return commands.length > 0 ? (
        <div data-automation-id={props.dataAutomationId}>
            <HeaderButton
                text={props.label}
                menuProps={menuProps}
                primary
                styles={{
                    root: {
                        width: 16,
                        padding: '0 !important',
                        margin: 0,
                        minWidth: props.minWidth ?? 68,
                        background: props.background,
                        borderColor: props.background,
                        color: props.color,
                        ":hover": {
                            background: props.background,
                            "filter": 'brightness(75%)',
                            color: props.color,
                        },
                    },
                    label: {
                        fontWeight: 600
                    }
                }}
                onRenderMenuIcon={props.hideCaret ? () => null : undefined}
                iconProps={{
                    iconName: props.iconName,
                    styles: {
                        root: {
                            fontWeight: 600,
                            padding: 0,
                            margin: 0,
                        }
                    }
                }}
            />
            {props.children}
        </div>
    ) : null;
}

export const HeaderButton = styled(DefaultButton) <{ hide?: boolean }>`
    font-family: ${dgFontFamily};
    font-size: 14px;
    border-radius: 5px;
    padding: 0 5px;
    ${props => !!props.hide ? 'display: none;' : ''}
`
